import { jwtDecode } from "jwt-decode"; // CHANGED: Named import
import { createContext, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import fetchWithLocale from "utils/fetchWithLocale";

// https://dev.to/finiam/predictable-react-authentication-with-the-context-api-g10
const AuthContext = createContext();
/**
 * We refer to token for validating that user is authenticated but keep the user profile in
 * a separate state
 *
 */
export const AuthProvider = ({ children }) => {
  // const [user, setUser] = useState();
  const [loading, setLoading] = useState(false);
  const domain = import.meta.env.VITE_API_URL;

  const navigate = useNavigate();

  const login = async (email_phone, password, redirect = true) => {
    setLoading(true);
    try {
      const res = await prepareFetch(
        `${domain}/v1/public/user/authenticate`,
        {
          method: "POST",
          body: JSON.stringify({
            email_phone,
            password,
          }),
        },
        redirect
      );

      if (res.accessToken) {
        setTokenAndRefreshToken(res);
        setLoading(false);
        navigate("/home", { replace: true });
        // return res; // Optional: you might want to return the result
      } else if (res.message) {
        // This block catches non-200 responses parsed by prepareFetch
        throw new Error(res.message);
      }
    } catch (error) {
      setLoading(false);
      // Check for the specific "unverified email" error message
      if (error.message.includes('verify your email')) {
        // Instead of just throwing, we will store the email/phone and redirect
        // We need to know which identifier the user used to log in
        // Let's assume it's the email for the resend flow. We might have to decrypt it on the backend.
        // Pass the identifier to the resend page
        navigate(`/verify-email?email_phone=${encodeURIComponent(email_phone)}`);
        // Throw the error so the Login component can still show a message if needed, but the redirect is the primary action.
        throw new Error(error.message);
      } else {
        // For all other errors (wrong password, user not found, etc.), just re-throw
        throw error;
      }
    }
  };
  const refreshToken = async (userid, refreshToken) => {
    // Get a token from api server using the fetch api
    const res = await prepareFetch(`${domain}/v1/public/user/refreshAccessToken`, {
      method: "POST",
      body: JSON.stringify({
        userid,
        refreshToken,
      }),
    });
    setTokenAndRefreshToken(res); // Setting the token in localStorage
    return res;
  };

  const loggedIn = () => {
    // Checks if there is a saved token and it's still valid
    const token = getToken(); // GEtting token from localstorage
    return !!token && !isTokenExpired(token); // handwaiving
  };

  const isTokenExpired = (token) => {
    try {
      const decoded = jwtDecode(token); // CHANGED: Use jwtDecode
      if (decoded.exp < Date.now() / 1000) {
        // Checking if token is expired. N
        return true;
      } else return false;
    } catch (err) {
      return false;
    }
  };

  const setTokenAndRefreshToken = (res) => {
    // Saves user token to localStorage
    sessionStorage.setItem("access_token", res.accessToken);
    sessionStorage.setItem("refresh_token", res.refreshToken);
  };

  const getToken = () => {
    // Retrieves the user token from localStorage
    return sessionStorage.getItem("access_token");
  };

  const logout = () => {
    // Clear user token and profile data from localStorage
    // localStorage.removeItem('access_token')
    // setUser(undefined);
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("refresh_token");
  };

  const getProfile = () => {
    const token = getToken()
    if (token) {
      // Using jwt-decode npm package to decode the token
      const decoded = jwtDecode(token); // CHANGED: Use jwtDecode
      return decoded.data;
    }
    return false
  };

  const user = getProfile()

  const prepareFetch = async (url, options) => {
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
    };

    if (loggedIn()) {
      headers["Authorization"] = "Bearer " + getToken();
    }

    const response = await fetchWithLocale(url, {
      headers,
      ...options,
    });

    // Check if the response is OK (status 200-299)
    if (!response.ok) {
      // If it's not OK, parse the error message and throw it
      const errorData = await response.json().catch(() => ({})); // Try to parse JSON error, fallback to empty object
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    // If it's OK, parse and return the JSON data
    return response.json();
  };

  // function checkStatus(response, redirect) {
  //   // raises an error in case response status is not a success
  //   if (response.status >= 200 && response.status < 300) {
  //     // Success status lies between 200 to 300
  //     return response;
  //   } else {
  //     // console.log(response)
  //     if (response.status === 401) {
  //       sessionStorage.removeItem("access_token");
  //       sessionStorage.removeItem("refresh_token");
  //       console.log('lets redirect')
  //       // by default on error will redirect back to login but if redirect is false then it is up to the caller to handle the situation, e.g. if we just want to display an error message on login form
  //       if (redirect) {
  //         navigate("/login");
  //       }
  //     }
  //     return response;
  //   }
  // }

  const resetPassword = async (email) => {
    const res = await prepareFetch(`${domain}/v1/public/user/password-reset`, {
      method: "POST",
      body: JSON.stringify({ email }),
    });
    return res;
  };

  const resendVerificationEmail = async (email_phone) => {
    // This calls the endpoint you created: /v1/public/user/confirm-token-reset
    // Note: The backend expects { email }. We assume the identifier used for login was the email.
    // If users can log in with phone, this becomes more complex.
    // You might need to adjust the backend or this function to handle phone numbers.
    const res = await prepareFetch(`${domain}/v1/public/user/confirm-token-reset`, {
      method: "POST",
      body: JSON.stringify({ email: email_phone }), // Sending as 'email'. Check your backend API.
    });
    // If the request was not successful, prepareFetch will throw an error
    return res; // Returns { message: 'success' }
  };

  const memoedValue = useMemo(
    () => ({
      user,
      loading,
      login,
      logout,
      getProfile,
      getToken,
      loggedIn,
      resetPassword,
      refreshToken,
      resendVerificationEmail
    }),
    [user, loading]
    // [loading, error]
  );

  return <AuthContext.Provider value={memoedValue}>{children}</AuthContext.Provider>;
};

export default function useAuth() {
  return useContext(AuthContext);
}